// src/components/character_editor/sections/RaceEditSection.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert
} from '@mui/material';
import {
  Groups as GroupsIcon,
  Psychology as PsychologyIcon,
  Biotech as BiotechIcon,
  Science as ScienceIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const RaceEditSection = ({ character, races, selectedRace, onRaceChange }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRaceDetails, setSelectedRaceDetails] = useState(null);

  const handleOpenDetails = (race) => {
    setSelectedRaceDetails(race);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedRaceDetails(null);
  };

  const handleSelectRace = (raceId) => {
    onRaceChange(raceId);
    handleCloseDetails();
  };

  // Функции перевода (аналогичные тем, что в создании персонажа)
  const translateEnvironment = (env) => {
    const environments = {
      'surface': 'Поверхность',
      'hydrosphere': 'Гидросфера', 
      'subterranean': 'Подземная',
      'atmosphere': 'Атмосфера',
      'space': 'Космос'
    };
    return environments[env] || env;
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <GroupsIcon sx={{ mr: 2 }} />
        Раса персонажа
      </Typography>

      {/* Текущая выбранная раса */}
      {selectedRace && (
        <Card elevation={3} sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {selectedRace.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedRace.biology?.description}
                </Typography>
              </Box>
              <Chip 
                label={translateEnvironment(selectedRace.environment)} 
                color="primary" 
                variant="outlined" 
              />
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Биологические особенности:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Продолжительность жизни: ${selectedRace.biology?.lifespan_years} лет`} size="small" />
                  <Chip label={`Технологический уровень: ${selectedRace.technology_level}`} size="small" />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Психологические особенности:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Любопытство: ${selectedRace.psychology?.curiosity}`} size="small" />
                  <Chip label={`Агрессия: ${selectedRace.psychology?.aggression}`} size="small" />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Выбор новой расы */}
      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Выберите новую расу
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Смена расы может повлиять на доступные навыки и характеристики персонажа
          </Typography>

          <Grid container spacing={2}>
            {races.map((race) => (
              <Grid item xs={12} md={6} key={race.id}>
                <Card 
                  variant={character.race === race.id ? "outlined" : "elevation"}
                  sx={{
                    border: character.race === race.id ? 2 : 0,
                    borderColor: 'primary.main',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    }
                  }}
                  onClick={() => handleOpenDetails(race)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {race.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {race.biology?.description?.substring(0, 100)}...
                        </Typography>
                      </Box>
                      {character.race === race.id && (
                        <Chip label="Текущая" color="primary" size="small" />
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                      <Chip label={translateEnvironment(race.environment)} size="small" variant="outlined" />
                      <Chip label={`Ур. ${race.technology_level}`} size="small" variant="outlined" />
                      <Chip label={`${race.biology?.lifespan_years} лет`} size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Диалог с деталями расы */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        {selectedRaceDetails && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupsIcon sx={{ mr: 2 }} />
                {selectedRaceDetails.name}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="body1" paragraph>
                    {selectedRaceDetails.biology?.description}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <BiotechIcon sx={{ mr: 1 }} />
                        Биология
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Среда обитания" 
                            secondary={translateEnvironment(selectedRaceDetails.environment)}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Продолжительность жизни" 
                            secondary={`${selectedRaceDetails.biology?.lifespan_years} лет`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Размножение" 
                            secondary={selectedRaceDetails.biology?.reproduction}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <PsychologyIcon sx={{ mr: 1 }} />
                        Психология
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary="Любопытство" 
                            secondary={selectedRaceDetails.psychology?.curiosity}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Агрессия" 
                            secondary={selectedRaceDetails.psychology?.aggression}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary="Кооперация" 
                            secondary={selectedRaceDetails.psychology?.cooperation}
                          />
                        </ListItem>
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScienceIcon sx={{ mr: 1 }} />
                        Технологии
                      </Typography>
                      <Typography variant="body1">
                        Технологический уровень: {selectedRaceDetails.technology_level}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Родная система: {selectedRaceDetails.home_system}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Отмена</Button>
              <Button 
                variant="contained" 
                onClick={() => handleSelectRace(selectedRaceDetails.id)}
                disabled={character.race === selectedRaceDetails.id}
              >
                {character.race === selectedRaceDetails.id ? 'Уже выбрана' : 'Выбрать эту расу'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default RaceEditSection;